import { KARAOKE_TRACK_NUMBER } from '@src/stores/VideoCreatorStore';
import { isSourceValueApproxEqual } from '@src/utility/approximateEquality';

import type { LowerThirdTemplate } from '@src/components/textProcessor/TextBrandingService';
import type { ElementState } from '@src/renderer/ElementState';
import type { RendererState } from '@src/renderer/RendererState';
import type { StateChangeContext } from '@src/stores/lib/StateChangeObserver';
import type VideoCreatorStore from '@src/stores/VideoCreatorStore';

const manualChangeFields: (keyof ElementState['source'])[] = ['x', 'y'];

// Track the previous deferred callback so we can cancel it before registering
// a new one. This prevents stale callbacks from accumulating during rapid drags
// and writing outdated position values.
let cancelPreviousDeferred: (() => void) | null = null;

function detectAndApplyManualChangesToTextConfig(
  videoCreator: VideoCreatorStore,
  newState: RendererState,
  context: StateChangeContext,
): boolean {
  // Only detect manual changes when the user is interacting with the renderer
  // iframe (canvas drag). Programmatic changes should not be re-applied from
  // the renderer echo.
  if (!context.isManualInteraction) return false;

  const activeElementOld = videoCreator.getActiveElement();
  if (
    !activeElementOld ||
    activeElementOld.source.type !== 'text' ||
    activeElementOld.source.track === KARAOKE_TRACK_NUMBER
  ) {
    return false;
  }

  const activeElementNew = newState.elements.find(
    el => el.source.id === activeElementOld.source.id,
  );
  if (!activeElementNew) {
    return false;
  }

  const propertyMap = manualChangeFields.reduce(
    (acc, field) => {
      // no change — use approximate comparison to absorb renderer round-trip
      // floating-point noise (e.g. "54%" echoed back as "53.99999999%")
      if (
        isSourceValueApproxEqual(
          activeElementNew.source[field],
          activeElementOld.source[field],
        )
      ) {
        return acc;
      }

      acc[field] = activeElementNew.source[field];
      return acc;
    },
    {} as Partial<ElementState['source']>,
  );

  if (Object.keys(propertyMap).length) {
    const cb = () => {
      // Check if we're moving TO the template position (applying template positioning)
      const selectedTemplate =
        videoCreator.textBrandingService.selectedBrandingTemplate;
      let shouldSkipModificationDetection = false;

      if (
        selectedTemplate &&
        videoCreator.textBrandingService.hasTemplatePositioning(
          selectedTemplate as LowerThirdTemplate,
        )
      ) {
        // Check if new position matches the template position
        const newElementMatchesTemplate =
          videoCreator.textBrandingService.doesElementMatchTemplatePosition(
            activeElementNew.source,
            selectedTemplate as LowerThirdTemplate,
          );

        // If new position matches the template, we're applying template positioning
        shouldSkipModificationDetection = newElementMatchesTemplate;
      }

      // Also skip if the flag is still set
      if (videoCreator.textBrandingService.isApplyingTemplatePosition) {
        shouldSkipModificationDetection = true;
      }

      if (!shouldSkipModificationDetection) {
        videoCreator.textBrandingService.checkPropsChanged(
          'basic',
          propertyMap,
        );
      }

      // Update the text producer config with the new position
      videoCreator.textProducer.setTextConfig({
        ...videoCreator.textProducer.getTextConfig(),
        ...propertyMap,
      });
    };

    // Cancel any previously deferred callback to prevent stale values from
    // accumulating. Only the latest drag position should be applied.
    if (cancelPreviousDeferred) {
      cancelPreviousDeferred();
      cancelPreviousDeferred = null;
    }

    if (videoCreator.rootStore.videoSourceStore.hasDebouncedSourceRefresh) {
      cancelPreviousDeferred =
        videoCreator.rootStore.videoSourceStore.onNextSourceRefresh(cb);
    } else {
      cb();
    }
  }

  return false;
}

detectAndApplyManualChangesToTextConfig.displayName =
  'detectAndApplyManualChangesToTextConfig';

export default detectAndApplyManualChangesToTextConfig;
